import React, { useEffect, useRef, useState } from 'react'
import { X, Upload, Database } from 'lucide-react'
import { projectsApi, AssetInventoryRow, VaiPreview } from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'
import { Button } from '@/components/ui/Button'
import { vaiReplaceSummary } from '@/lib/vaiSummary'
import { useTranslation } from 'react-i18next'

interface Props {
  projectSystemId: string
  initialFile?: File | null
  onClose: () => void
  onSuccess: () => void
}

interface ParseState {
  rows:          AssetInventoryRow[]
  vessel_header: VaiPreview['vessel_header']
  row_count:     number
}

export const ImportSystemAssetInventoryModal: React.FC<Props> = ({ projectSystemId, initialFile, onClose, onSuccess }) => {
  const { t } = useTranslation()
  const { error: showError, success } = useToast()
  const [file, setFile] = useState<File | null>(null)
  const [parseResult, setParseResult] = useState<ParseState | null>(null)
  const [parsing, setParsing] = useState(false)
  const [committing, setCommitting] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const initialParseStarted = useRef(false)

  const handleFileChange = async (f: File | null) => {
    setFile(f)
    setParseResult(null)
    if (!f) return

    setParsing(true)
    try {
      const result = await projectsApi.parseSystemAssetInventory(projectSystemId, f)
      setParseResult({
        rows:          result.rows,
        vessel_header: result.vessel_header,
        row_count:     result.row_count,
      })
    } catch (err: any) {
      showError(err.message ?? t('importModal.parseFailed'))
      setFile(null)
    } finally {
      setParsing(false)
    }
  }

  useEffect(() => {
    if (initialFile && !initialParseStarted.current) {
      initialParseStarted.current = true
      void handleFileChange(initialFile)
    }
    // The parent keys the modal by route/file; parse an initial drop once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCommit = async () => {
    if (!parseResult) return
    setCommitting(true)
    try {
      const result = await projectsApi.commitSystemAssetInventory(projectSystemId, {
        vai_rows:        parseResult.rows,
        vessel_header:   parseResult.vessel_header,
        source_filename: file?.name,
      })
      success(`Inventory imported: ${vaiReplaceSummary(result.vai_deleted, result.vai_inserted, result.vai_skipped)}`)
      onSuccess()
    } catch (err: any) {
      showError(err.message ?? t('importModal.importFailed'))
    } finally {
      setCommitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold">{t('projectDetail.importInventory')}</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!parseResult && (
            <div
              onDragEnter={e => { e.preventDefault(); e.stopPropagation(); setDragActive(true) }}
              onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragActive(true) }}
              onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setDragActive(false) }}
              onDrop={e => {
                e.preventDefault(); e.stopPropagation(); setDragActive(false)
                const f = e.dataTransfer.files?.[0]
                if (f) handleFileChange(f)
              }}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                dragActive
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-slate-300 dark:border-slate-600 hover:border-blue-400'
              } ${parsing ? 'opacity-60 pointer-events-none' : ''}`}
              onClick={() => document.getElementById('system-vai-file-input')?.click()}
            >
              <Upload className={`h-8 w-8 mx-auto mb-2 ${dragActive ? 'text-blue-500' : 'text-slate-400'}`} />
              <div className="text-sm font-medium">
                {parsing ? t('importModal.parsing') : file ? file.name : dragActive ? t('importModal.dropHere') : t('importModal.dropWorkbook')}
              </div>
              <div className="text-xs text-slate-400 mt-1">.xlsx file with sheet 3 (Vessel Asset Inventory). Rows attach to this system.</div>
              <input
                id="system-vai-file-input"
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={e => handleFileChange(e.target.files?.[0] ?? null)}
              />
            </div>
          )}

          {parseResult && (
            <div className="p-3 border border-sky-200 bg-sky-50 dark:bg-sky-900/20 dark:border-sky-800 rounded-lg">
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-sky-600" />
                <span className="text-sm font-medium text-sky-800 dark:text-sky-300">
                  {t('importModal.rowsReady', { count: parseResult.row_count })}
                </span>
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                All rows will be attached to this system. Rows that collide on item number with existing rows on this system are skipped automatically.
              </div>
              {parseResult.vessel_header.vessel_name && (
                <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-2">
                  Vessel: {parseResult.vessel_header.vessel_name}
                  {parseResult.vessel_header.imo_number && ` · IMO ${parseResult.vessel_header.imo_number}`}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-700">
          <Button variant="secondary" onClick={onClose} disabled={committing}>{t('common.cancel')}</Button>
          {parseResult && (
            <Button onClick={handleCommit} disabled={committing || parseResult.row_count === 0}>
              {committing ? t('importModal.importing') : t('common.import')}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
